<template>
    <div class="ds-schedule-page">
        <Toolbar :style="{ width: canvasStyle.width }" @switch="onSwitch" />
        <div ref="scheduleViewport" class="ds-schedule-viewport">
            <div class="schedule-scroll-content">
            <div class="ds-schedule-canvas" :style="canvasStyle">
                <div class="sticky top-0 z-40">
                    <div class="ds-schedule-header-row">
                        <div class="ds-schedule-header-employee-spacer sticky left-0 flex bg-grayxlight z-10">
                            <div class="w-[200px] h-12 flex items-center relative">
                                <div class="schedule-search mx-1.5">
                                    <SearchInput v-model="searchQuery" @clear="searchQuery = ''" />
                                </div>
                                <dropdown-radio :options="sortOptions" v-model="userSort" @change="onUserSort">
                                    <button-icon class="bg-white text-gray hover:text-darkblue" width="17" height="17">
                                        <IconFilter />
                                    </button-icon>
                                </dropdown-radio>
                            </div>
                            <div v-if="store.isEmployeeStatsVisible" class="pl-2 pt-2 text-xs">
                                <div class="text-darkred relative tooltip-trigger">
                                    <dropdown-radio :options="userStatOptions" :show-tooltip="true" @change="onUserStat"
                                        v-model="userStat">{{ userStat?.abbr }}</dropdown-radio>
                                </div>
                                <div class="text-darkblue relative tooltip-trigger">
                                    <dropdown-radio :options="userStatOptions" :show-tooltip="true" @change="onUserStat"
                                        v-model="userStat2">{{ userStat2?.abbr }}</dropdown-radio>
                                </div>
                            </div>
                        </div>
                        <div class="ds-schedule-header-days">
                            <div v-for="day in calendar" :key="day.date" class="ds-schedule-header-cell group"
                                :class="{ 'ds-schedule-header-cell-today': +dayjs(day.date).format('D') === +today && store.currentScheduleDate.format('MM') === dayjs().format('MM') }">
                                <div :class="{ 'schedule-holiday': day.is_weekend || day.is_holiday }" class="relative schedule-day-tooltip-trigger" tabindex="0">
                                    <span class="ds-schedule-header-date">{{ dayjs(day.date).format('DD.MM') }}</span>
                                    <span class="ds-schedule-header-weekday">{{ day.week_day.toUpperCase() }}</span>
                                    <Tooltip :text="day.note || day.day_type_text" placement="bottom" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="ds-schedule-weeks-row">
                    <div class="ds-schedule-weeks-spacer"></div>
                    <div class="ds-schedule-weeks-days">
                        <div v-for="week in isoWeeks" :key="week.day" class="ds-schedule-weeks-cell"
                            :class="{ 'ds-schedule-weeks-cell-today': week.day === +today && store.currentScheduleDate.format('MM') === dayjs().format('MM') }">
                            <span v-if="week.isMonday" class="ds-schedule-weeks-label">{{ week.number }}</span>
                        </div>
                    </div>
                </div>

                <ScheduleVacancies
                    v-if="switcher && authStore.permissions?.can_view_vacancies"
                    ref="scheduleVacancies"
                    :days
                    :today
                    @create="openVacancyPanel"
                />

                <div v-if="store.isDayStatsVisible" class="ds-schedule-day-stats-row">
                    <div class="ds-schedule-day-stats-spacer sticky left-0 flex justify-end bg-white">
                        <div class="pt-1 text-xs text-right">
                            <div class="text-darkred text-left">
                                <dropdown-radio :options="dayStatOptions" @change="onDayStat" v-model="dayStat">{{ cut(dayStat?.title, 29) }}</dropdown-radio>
                            </div>
                            <div class="text-darkblue text-left">
                                <dropdown-radio :options="dayStatOptions" @change="onDayStat" v-model="dayStat2">{{ cut(dayStat2?.title, 29) }}</dropdown-radio>
                            </div>
                        </div>
                        <div class="pt-1 pl-2 w-11 text-xs pb-1">
                            <div class="text-darkred text-center">{{ formatDayStatValue(statSum) }}</div>
                            <div class="text-darkblue text-center">{{ formatDayStatValue(statSum2) }}</div>
                        </div>
                    </div>
                    <div class="ds-schedule-day-stats-days">
                        <div v-for="day in days" :key="day" class="ds-schedule-day-stats-cell text-xs text-center pt-1 bg-white"
                            :class="{ 'ds-schedule-day-stats-cell-today': day === +today && store.currentScheduleDate.format('MM') === dayjs().format('MM') }">
                            <div class="text-darkred">{{ formatDayStatValue(dayStats[day]) }}</div>
                            <div class="text-darkblue">{{ formatDayStatValue(dayStats2[day]) }}</div>
                        </div>
                    </div>
                </div>

                <ScheduleTable v-if="switcher" :days :today :schedules="filteredSchedules" @day-clicked="openSidePanel($event)"
                    @user-clicked="openUserPanel($event)" :key="store.refresh" />

                <TimeSheetTable v-else :days :today :schedules="filteredSchedules" @day-clicked="editTimesheet($event)" />
            </div>
            <div
                v-if="sidebarSpacerWidth"
                class="shrink-0"
                :style="{ width: `${sidebarSpacerWidth}px` }"
                aria-hidden="true"
            />
            </div>
        </div>
        <div v-if="switcher" class="ds-schedule-statbar-shell">
            <div class="ds-schedule-canvas">
                <StatBar />
            </div>
        </div>
    </div>
    <SidePanel ref="sidebar" @close="restoreScheduleScroll" @opened="scrollEditedDayIntoView">
        <div v-if="loading" class="flex items-center h-full justify-center">
            <Loader />
        </div>
        <ShiftInfo ref="shiftInfo" v-else :scheduleIndex="scheduleIndex" :day="workday" @close="closeSidePanel" @switch="onShiftSwitch" />
    </SidePanel>

    <SidePanel ref="usercard">
        <div v-if="loading" class="flex items-center h-full justify-center">
            <Loader />
        </div>
        <EmployeeInfo @cancel="getEmployment" :schedule="userSchedule" :employment v-else />
    </SidePanel>

    <SidePanel ref="vacancySidebar">
        <AddVacancy
            :initial-values="vacancyInitialValues"
            @close="closeVacancyPanel"
            @saved="reloadVacancies"
        />
    </SidePanel>

    <modal v-if="showModal" @close="cancelShiftSwitch()">
            <div class="w-[300px]">
                <div class="text-xl mb-3">Сохранить изменения?</div>
                <div class="flex justify-end">
                    <btn class="px-5 mx-3" @click="saveCurrentShift">Да</btn>
                    <btn class="px-5" type="secondary" @click="discardCurrentShift">Нет</btn>
                </div>
            </div>
    </modal>

</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, nextTick, onBeforeUpdate, watch } from 'vue'
import Toolbar from '../components/header/Toolbar.vue'
import IconFilter from '../ui/icons/IconFilter.vue'
import dropdownRadio from '../components/dropdownRadio.vue'
import Tooltip from '../components/Tooltip.vue'
import StatBar from '../components/schedule/StatBar.vue'
import SidePanel from '../components/schedule/SidePanel.vue'
import ShiftInfo from '../components/schedule/ShiftInfo.vue'
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import isoWeek from 'dayjs/plugin/isoWeek'
import ScheduleTable from '../components/schedule/ScheduleTable.vue'
import ScheduleVacancies from '@/components/schedule/ScheduleVacancies.vue'
import EmployeeInfo from '@/components/employee/EmployeeInfo.vue'
import TimeSheetTable from '@/components/timesheet/TimeSheetTable.vue'
import Loader from '@/ui/Loader.vue'
import { useAuthStore } from '@/stores/authStore'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useEmploymentStore } from '@/stores/employmentStore'
import AddVacancy from '@/components/exchange/AddVacancy.vue'
import SearchInput from '@/components/SearchInput.vue'
import type { Option, Schedule } from '@/types'

dayjs.locale('ru')
dayjs.extend(utc)
dayjs.extend(isoWeek)

const store = useScheduleStore()
const employmentStore = useEmploymentStore()
const authStore = useAuthStore()

const stats = computed(() => store.stats)

const userStatOptions = computed(() => {
    const options: Array<{ title: string, value: string, abbr: string, description?: string }> = []
    if (store.stats) {
        const stats = store.stats
        for (let stat in stats) {
            options.push({
                title: stats[stat].name,
                value: stat,
                abbr: stats[stat].abbreviation,
                description: stats[stat].description
            })
        }
        return options
    }
    return []
})

const dayStatOptions = [
    { title: 'Часов по плану', value: 'plan-hours' },
    { title: 'Часы собственных сотрудников', value: 'employee-hours' },
    { title: 'Часы внешних сотрудников', value: 'outsource-hours' },
    { title: 'Количество работающих сотрудников', value: 'employees' }
]

const sortOptions = [
    { title: 'Сортировка по имени', value: 'name' },
    { title: 'Сортировка по должности', value: 'position' }
]

const userSort = ref(sortOptions[0])

const switcher = ref(true)
const searchQuery = ref('')
const userStat = ref()
const userStat2 = ref()
const dayStat = ref(dayStatOptions[0])
const dayStat2 = ref(dayStatOptions[0])
const statSum = ref(0)
const statSum2 = ref(0)
const scheduleIndex = ref(0)
const workday = ref()
const selectedScheduleCell = ref<HTMLElement | null>(null)
const loading = ref(false)
const dayStats = ref<any[]>([])
const dayStats2 = ref<any[]>([])
const showModal = ref(false)
const savedScheduleScrollLeft = ref<number | null>(null)
const sidebarSpacerWidth = ref(0)
let spacerResetTimer: number | undefined

const cut = (text: string, num: number) => text.slice(0, num)
const formatDayStatValue = (value: unknown) => {
    const numeric = Number(value)
    if (!Number.isFinite(numeric)) return 0
    return Math.round(numeric)
}
const sidebar = ref<{ toggle: () => void; panel: HTMLElement | null }>()
const scheduleViewport = ref<HTMLElement | null>(null)
const usercard = ref()
const vacancySidebar = ref()
const scheduleVacancies = ref<{ reload: () => Promise<void> }>()
const userSchedule = ref()
const shiftInfo = ref()
const vacancyInitialValues = ref<{
    date: string
    department: Option
    inner: boolean
    outer: boolean
}>()

const employment = computed(() => employmentStore.selectedEmployment)

const onSwitch = (s: boolean) => {
    switcher.value = s
}

const resetShiftsChanges = () => {
    if (scheduleIndex.value >= 0 && scheduleIndex.value < store.schedules.length) {
        const targetSchedule = store.schedules[scheduleIndex.value];
        targetSchedule.shifts = targetSchedule.shifts.map(shift => {
            const replacement = store.originalShifts.find(os => os.id === shift.id);
            return replacement || shift;
        });
    }
    store.isCurrentShiftChanged = false
}

const openSidePanel = (data: any) => {
    const { day, index, cell } = data
    scheduleIndex.value = index
    workday.value = day
    selectedScheduleCell.value = cell instanceof HTMLElement ? cell : null
    if(store.isCurrentShiftChanged) resetShiftsChanges()
    if (spacerResetTimer) window.clearTimeout(spacerResetTimer)
    sidebarSpacerWidth.value = 0
    const viewport = scheduleViewport.value
    savedScheduleScrollLeft.value = viewport?.scrollLeft ?? null
    sidebar.value?.toggle()
}

const scrollEditedDayIntoView = () => {
    const viewport = scheduleViewport.value
    const target = selectedScheduleCell.value
    const panel = sidebar.value?.panel
    if (!viewport || !target || !panel) return

    const getRequiredShift = () => {
        const targetRect = target.getBoundingClientRect()
        const sidebarRect = panel.getBoundingClientRect()
        const cellWidth = targetRect.width
        const hasNextDay = target.nextElementSibling !== null
        const requiredGap = hasNextDay ? cellWidth : 0

        return targetRect.right + requiredGap - sidebarRect.left
    }

    const scrollToContext = () => {
        const requiredShift = getRequiredShift()
        if (requiredShift <= 0) return

        const targetScrollLeft = viewport.scrollLeft + requiredShift
        const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth)
        if (targetScrollLeft > maxScroll) {
            sidebarSpacerWidth.value += targetScrollLeft - maxScroll
            nextTick(() => requestAnimationFrame(scrollToContext))
            return
        }

        viewport.scrollTo({ left: targetScrollLeft, behavior: 'smooth' })
    }

    scrollToContext()
}

const closeSidePanel = () => {
    sidebar.value?.toggle()
    restoreScheduleScroll()
}

const restoreScheduleScroll = () => {
    const viewport = scheduleViewport.value
    const scrollLeft = savedScheduleScrollLeft.value
    if (viewport && scrollLeft !== null) {
        viewport.scrollTo({ left: scrollLeft, behavior: 'smooth' })
    }
    savedScheduleScrollLeft.value = null
    if (spacerResetTimer) window.clearTimeout(spacerResetTimer)
    spacerResetTimer = window.setTimeout(() => {
        sidebarSpacerWidth.value = 0
    }, 350)
}

const openVacancyPanel = (data: { day: number; channel: 'outer' | 'inner' }) => {
    const department = store.currentDepartment
    if (!department || !authStore.permissions?.can_create_vacancy_exchange) return

    vacancyInitialValues.value = {
        date: store.currentScheduleDate.date(data.day).format('YYYY-MM-DD'),
        department: { id: department.id, name: department.name },
        inner: data.channel === 'inner',
        outer: data.channel === 'outer',
    }
    vacancySidebar.value.toggle()
}

const closeVacancyPanel = () => {
    vacancySidebar.value.toggle()
}

const reloadVacancies = () => scheduleVacancies.value?.reload()

const getEmployment = async () => {
    loading.value = true
    await employmentStore.fetchEmployment(userSchedule.value.id)
    loading.value = false
}

const openUserPanel = async (data: any) => {
    usercard.value.toggle()
    userSchedule.value = data
    await getEmployment()
}

const today = dayjs().format('D')

const days = computed(() => store.currentScheduleDate.daysInMonth())
const filteredSchedules = computed(() => {
    const query = searchQuery.value.trim().toLocaleLowerCase()
    if (!query) return store.schedules

    return store.schedules.filter((schedule: Schedule) => {
        const nameParts = [schedule.user?.last_name, schedule.user?.first_name, schedule.user?.middle_name]
            .filter(Boolean)
        const fullName = nameParts.join(' ').toLocaleLowerCase()
        const reverseFullName = [schedule.user?.first_name, schedule.user?.last_name, schedule.user?.middle_name]
            .filter(Boolean)
            .join(' ')
            .toLocaleLowerCase()
        const positionName = schedule.position?.name?.toLocaleLowerCase() ?? ''

        return fullName.includes(query) || reverseFullName.includes(query) || positionName.includes(query)
    })
})
const calendar = computed(() => store.prodCalendar)
const isoWeeks = computed(() => Array.from({ length: days.value }, (_, index) => {
    const date = store.currentScheduleDate.date(index + 1)
    return {
        day: index + 1,
        number: date.isoWeek(),
        isMonday: date.isoWeekday() === 1,
    }
}))
const canvasStyle = computed(() => ({
    width: `calc(var(--schedule-employee-column-width) + ${days.value} * var(--schedule-day-cell-width))`,
    minWidth: `calc(var(--schedule-employee-column-width) + ${days.value} * var(--schedule-day-cell-width))`,
}))

const calcStat = (day: any, stat: any) => {
    if (stat.value === 'plan-hours') {
        const schedules = store.schedules.filter(item => item.shifts.length && item.shifts[0].department === store.currentDepartment?.id)
        const shifts = dayShifts(schedules, day)
        if (shifts.length) {
            return shiftsHours(shifts)
        }
        return 0
    }
    if (stat.value === 'employee-hours') {
        const schedules = store.schedules.filter(item => item.department.id === store.currentDepartment?.id)
        const shifts = dayShifts(schedules, day)
        if (shifts.length) {
            return shiftsHours(shifts)
        }
        return 0
    }
    if (stat.value === 'outsource-hours') {
        const schedules = store.schedules.filter(item => item.department.id !== store.currentDepartment?.id)
        const shifts = dayShifts(schedules, day)
        if (shifts.length) {
            return shiftsHours(shifts)
        }
        return 0
    }
    if (stat.value === 'employees') {
        const shifts = dayShifts(store.schedules, day)
        return [... new Set(shifts.map((shift: any) => shift.employment))].length
    }
}

function onDayStat() {
    localStorage.setItem('dayStat', JSON.stringify(dayStat.value))
    localStorage.setItem('dayStat2', JSON.stringify(dayStat2.value))
    dayStats.value = []
    calcDayStats(dayStats.value, dayStat.value)
    statSum.value = dayStats.value.reduce((sum, item) => sum + item, 0)
    dayStats2.value = []
    calcDayStats(dayStats2.value, dayStat2.value)
    statSum2.value = dayStats2.value.reduce((sum, item) => sum + item, 0)
}

function calcDayStats(arrStats: any[], stat: any) {
    for (let day = 0; day <= days.value; day++) {
        arrStats.push(calcStat(day, stat))
    }
}

function shiftsHours(shifts: any) {
    return shifts.reduce((sum: any, item: any) => {
        return sum + getShiftDurationHours(item)
    }, 0)
}

function getShiftDurationHours(shift: any) {
    const duration = Number(shift?.duration)
    if (Number.isFinite(duration) && duration >= 0) return duration

    if (!shift?.plan_work_start_at || !shift?.plan_work_end_at) return 0

    const start = dayjs(shift.plan_work_start_at)
    let end = dayjs(shift.plan_work_end_at)

    if (!start.isValid() || !end.isValid()) return 0

    if (end.isSame(start) || end.isBefore(start)) {
        end = end.add(1, 'day')
    }

    return end.diff(start, 'minute') / 60
}

function dayShifts(schedules: any, day: any) {
    return schedules.map((item: any) => item.shifts.filter((shift: any) => +dayjs(shift.date).format('D') == day)).flat()
}

function getDayStatsFromLocalStorage() {
    const ds = localStorage.getItem('dayStat')
    const ds2 = localStorage.getItem('dayStat2')
    if (ds) (dayStat.value = JSON.parse(ds))
    if (ds2) (dayStat2.value = JSON.parse(ds2))
}

function onUserStat() {
    store.userStat = userStat.value
    store.userStat2 = userStat2.value
    localStorage.setItem('userStat', JSON.stringify(userStat.value))
    localStorage.setItem('userStat2', JSON.stringify(userStat2.value))
}

function getUserStatsFromLocalStorage() {
    const options = userStatOptions.value
    const getStoredOption = (key: string) => {
        const stored = localStorage.getItem(key)
        if (!stored) return options[0]

        try {
            const value = JSON.parse(stored)?.value
            return options.find(option => option.value === value) ?? options[0]
        } catch {
            return options[0]
        }
    }

    userStat.value = getStoredOption('userStat')
    userStat2.value = getStoredOption('userStat2')
    store.userStat = userStat.value
    store.userStat2 = userStat2.value

    if (userStat.value) localStorage.setItem('userStat', JSON.stringify(userStat.value))
    else localStorage.removeItem('userStat')
    if (userStat2.value) localStorage.setItem('userStat2', JSON.stringify(userStat2.value))
    else localStorage.removeItem('userStat2')
}

const editTimesheet = (e: any) => {
    if (authStore.permissions?.can_edit_timesheet) openSidePanel(e)
}

const onUserSort = () => {
    if(userSort.value.value === 'position') {
        store.schedules = store.schedulesSortedByPosition
    } else {
        store.schedules = store.schedulesSortedByName
    }
    store.refresh++
}

const onShiftSwitch = () => {
    showModal.value = true
}

const discardCurrentShift = () => {
    loading.value = true
    // reset changes
    resetShiftsChanges()
    setTimeout(() => loading.value = false, 0)
    showModal.value = false
    shiftInfo.value?.completePendingShiftSwitch()
}

const cancelShiftSwitch = () => {
    showModal.value = false
    shiftInfo.value?.cancelPendingShiftSwitch()
}

const saveCurrentShift = async () => {
    if (!shiftInfo.value?.validateCurrentShiftPlanTime()) {
        showModal.value = false
        shiftInfo.value?.cancelPendingShiftSwitch()
        return
    }

    const savedShifts = await store.shiftPackCreateUpdate([store.currentShift])
    if (!savedShifts) return

    store.isCurrentShiftChanged = false
    store.isSaveShiftEnabled = false
    showModal.value = false
    shiftInfo.value?.completePendingShiftSwitch()
}

watch(stats, () => getUserStatsFromLocalStorage())

onMounted(async () => {
    getDayStatsFromLocalStorage()
})

onBeforeUnmount(() => {
    if (spacerResetTimer) window.clearTimeout(spacerResetTimer)
})

onBeforeUpdate(() => {
    onDayStat()
})
</script>

<style scoped>
.schedule-scroll-content {
    display: flex;
    width: max-content;
    min-width: 100%;
}

.schedule-search {
    --search-width: 160px;
    --search-height: 24px;
    --search-icon-size: 15px;
    --search-bg: var(--color-bg-surface);
    --search-border: var(--color-bg-surface);
}

.schedule-search :deep(.ds-search-field > div) {
    width: var(--search-height);
    height: var(--search-height);
}

.schedule-search :deep(.ds-search-field > div svg) {
    width: var(--search-icon-size);
    height: var(--search-icon-size);
}

</style>
